import cn from 'classnames';
import { useMemo } from 'react';
import { Table } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { ChartContainer } from '@Components/ChartContainer';
import { Appearance } from '@Components/designConstants';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { HierarchyLevelConst } from '../../dtc-commerce-mmm-types';
import {
  formatCurrency,
  formatDollarsExact,
  formatWholeNumber
} from '../../utils/dtc-commerce-mmm-format';
import { subChannelDisplayName } from '../../utils/dtc-commerce-mmm-channel-helpers';
import { ChannelIcon } from '../ChannelIcon';
import { IncrementalityBeaker } from '../IncrementalityBeaker';
import { MediaTypeBadge, MediaTypeBadgeVariant } from '../MediaTypeBadge';
import { SubChannelInfo } from './channel-drawer-types';

interface ChannelDrawerSubChannelsProps {
  subChannels: SubChannelInfo[];
  isRevenue: boolean;
  focusedNames: Set<string>;
  canFocus: boolean;
  onToggleFocus: (name: string) => void;
  onClearFocus: () => void;
}

const formatEfficiency = (value: number | null): string => formatDollarsExact(value);

export const ChannelDrawerSubChannels = ({
  subChannels,
  isRevenue,
  focusedNames,
  canFocus,
  onToggleFocus,
  onClearFocus
}: ChannelDrawerSubChannelsProps) => {
  const hasFocus = focusedNames.size > 0;
  const sorted = useMemo(
    () => [...subChannels].sort((a, b) => b.effectiveNormalized - a.effectiveNormalized),
    [subChannels]
  );

  const kpiLabel = isRevenue ? 'Rev.' : 'Conv.';
  const efficiencyLabel = isRevenue ? 'ROAS' : 'CPA';

  const subtitle = (() => {
    const count = conditionallyPluralizeWithCount(subChannels.length, 'sub-channel');
    if (!canFocus) return count;
    return hasFocus
      ? `${count} — click rows to add or remove from focus`
      : `${count} — click rows to focus charts on specific sub-channels`;
  })();

  return (
    <ChartContainer
      title={
        <div className='dtc-commerce-mmm-title-stack'>
          <div>Sub-channels</div>
          <div className='dtc-commerce-mmm-title-stack-sub'>{subtitle}</div>
        </div>
      }
      viewRightActions={canFocus && hasFocus}
      rightActions={
        canFocus && hasFocus ? (
          <Button appearance={Appearance.EMPTY} onClick={onClearFocus}>
            Clear focus
          </Button>
        ) : undefined
      }
    >
      <div className='channel-drawer-chart-stack'>
        <Table size='sm' hover={canFocus} className='channel-drawer-sub-channels-table'>
          <thead>
            <tr>
              <th className='align-left'>Name</th>
              <th className='align-left'>Type</th>
              <th className='align-right'>Spend</th>
              <th className='align-right'>{kpiLabel}</th>
              <th className='align-right'>{efficiencyLabel}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(sc => {
              const isFocused = focusedNames.has(sc.name);
              const isDimmed = hasFocus && !isFocused;
              return (
                <tr
                  key={sc.name}
                  className={cn({
                    focused: isFocused,
                    dimmed: isDimmed,
                    clickable: canFocus
                  })}
                  onClick={canFocus ? () => onToggleFocus(sc.name) : undefined}
                >
                  <td className='align-left'>
                    <span className='channel-drawer-sub-channel-name'>
                      {isFocused && <span className='channel-drawer-focus-dot' />}
                      <ChannelIcon
                        channel={sc.channel}
                        platform={sc.platform}
                        level={HierarchyLevelConst.PLATFORM}
                        size={14}
                      />
                      <span className={cn({ subtle: sc.isDark })}>
                        {subChannelDisplayName(sc.name)}
                      </span>
                      {sc.incrementalityTest && <IncrementalityBeaker size={14} />}
                      {sc.isDark && <span className='channel-drawer-tag'>🌙 Dark</span>}
                    </span>
                  </td>
                  <td className='align-left'>
                    <MediaTypeBadge
                      mediaType={sc.mediaType}
                      variant={MediaTypeBadgeVariant.SHORT}
                    />
                  </td>
                  <td className='align-right tabular'>{formatCurrency(sc.rawSpend)}</td>
                  <td className='align-right tabular'>
                    {isRevenue
                      ? formatCurrency(sc.effectiveNormalized)
                      : formatWholeNumber(sc.effectiveNormalized)}
                  </td>
                  <td className='align-right tabular'>{formatEfficiency(sc.efficiency)}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </ChartContainer>
  );
};
